import {
  wineTypeSchema,
  type AreaReport,
  type AreaReportSummary,
  type OpenAreaWishGroup,
  type RunAreaReportRequest,
  type WineType,
} from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  ApiError,
  declineAreaWishes,
  deleteAreaReport,
  fetchAllAreaReports,
  fetchAreaReport,
  fetchOpenAreaWishes,
  publishAreaReport,
  unpublishAreaReport,
  runAreaReport,
} from "../api.js";
import { AreaReview } from "../components/AreaReview.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { CountrySelect } from "../components/CountrySelect.js";
import {
  actionButton,
  control,
  fieldLabel,
  resting,
  Select,
} from "../components/controls.js";
import { Flag } from "../components/Flag.js";
import { useCellarCountries } from "../cellar-countries.js";
import { countryName, orderCountries } from "../countries.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";
import { useSession } from "../session.js";

type State =
  | { status: "loading" }
  | { status: "ready"; canRun: boolean; reports: AreaReportSummary[] }
  | { status: "failed" };

/** One kind, country and area, and every run of it, newest first. */
type Group = {
  readonly kind: WineType;
  readonly countryCode: string;
  readonly area: string;
  readonly runs: readonly AreaReportSummary[];
};

const KINDS = wineTypeSchema.options;

/**
 * *Områdesrapporter* — every area report there has been, and running a new
 * one. Task 10.32; decided 2026-09-28 in `project-scope.md`.
 *
 * **The administrator's alone**, reached from *Inställningar*, because running
 * the question is theirs and the page is about every report in the system
 * rather than about a cellar.
 *
 * **Grouped by what readers find a report by** — kind, then country and area —
 * so each group is one entry in the readers' tree, with what is published in
 * it and every draft beside it. A draft is read on the same page a reader
 * will read it on, so what is published is what was read.
 *
 * **A run takes half a minute**, and the page says so while it waits rather
 * than pretending to be done. One run at a time from here: the buttons are
 * dark while one is out.
 */
export function AreaReports() {
  const { t } = useTranslation();
  const session = useSession();
  const [state, setState] = useState<State>({ status: "loading" });
  const [running, setRunning] = useState<RunAreaReportRequest | null>(null);
  const [message, setMessage] = useState<{
    tone: "done" | "failed";
    text: string;
  } | null>(null);

  // Bumped whenever the reports are read again, so the wishes are too: a
  // publish meets them, and the list above should say so at once.
  const [wishesVersion, setWishesVersion] = useState(0);

  const load = useCallback(() => {
    void fetchAllAreaReports()
      .then(({ canRun, reports }) => {
        setState({ status: "ready", canRun, reports });
        setWishesVersion((version) => version + 1);
      })
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  if (session.status === "signedIn" && !session.user.isAdministrator) {
    return (
      <section>
        <CellarHeading cellarId={null} current="areaReports" />
        <p className="text-muted mt-6 max-w-prose text-sm">{t("settings.notYours")}</p>
      </section>
    );
  }

  const run = (request: RunAreaReportRequest) => {
    setRunning(request);
    setMessage(null);
    // The run is a row on the server a moment after it starts; reading the
    // list again then puts *Körs …* on its group, where it belongs.
    window.setTimeout(load, 1500);
    void runAreaReport(request)
      .then((report) => {
        setMessage({ tone: "done", text: t("areaReports.ran", { area: report.area }) });
        load();
      })
      .catch((error: unknown) => {
        const code = error instanceof ApiError ? error.code : "";
        setMessage({
          tone: "failed",
          text:
            code === "switched_off"
              ? t("areaReports.switchedOff")
              : code === "not_configured"
                ? t("areaReports.notConfigured")
                : t("areaReports.runFailed"),
        });
        load();
      })
      .finally(() => setRunning(null));
  };

  const publish = (report: AreaReportSummary) => {
    setMessage(null);
    void publishAreaReport(report.id)
      .then(() => {
        setMessage({
          tone: "done",
          text: t("areaReports.published", { area: report.area }),
        });
        load();
      })
      .catch(() => setMessage({ tone: "failed", text: t("areaReports.actionFailed") }));
  };

  const unpublish = (report: AreaReportSummary) => {
    setMessage(null);
    void unpublishAreaReport(report.id)
      .then(() => {
        setMessage({
          tone: "done",
          text: t("areaReports.unpublished", { area: report.area }),
        });
        load();
      })
      .catch(() => setMessage({ tone: "failed", text: t("areaReports.actionFailed") }));
  };

  const remove = (report: AreaReportSummary) => {
    setMessage(null);
    void deleteAreaReport(report.id)
      .then(load)
      .catch(() => setMessage({ tone: "failed", text: t("areaReports.actionFailed") }));
  };

  const reports = state.status === "ready" ? state.reports : [];
  const canRun = state.status === "ready" && state.canRun && running === null;

  return (
    <section className="space-y-10">
      <div>
        {/* The heading every cellar's page wears, since 2026-10-08, so this
            page is reached from *Mer* and the way on from it is the menu. The
            link to *Inställningar* that stood above it went the same day, when
            that page became an entry in *Mer* too. */}
        <CellarHeading cellarId={null} current="areaReports" />
        <p className="text-muted mt-2 max-w-prose text-sm">{t("areaReports.intro")}</p>
      </div>

      {state.status === "loading" && <p className="text-muted text-sm">{t("loading")}</p>}
      {state.status === "failed" && (
        <p role="alert" className="text-past text-sm">
          {t("areaReports.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          <OpenWishes key={wishesVersion} reports={reports} canRun={canRun} onRun={run} />

          <RunForm
            reports={reports}
            canRun={canRun}
            available={state.canRun}
            running={running}
            onRun={run}
          />

          {message !== null && (
            <p
              role={message.tone === "failed" ? "alert" : "status"}
              className={`text-sm ${message.tone === "failed" ? "text-past" : "text-ready"}`}
            >
              {message.text}
            </p>
          )}

          <section className="space-y-6">
            <h2 className="font-display text-xl">{t("areaReports.reports")}</h2>
            {reports.length === 0 && (
              <p className="text-muted text-sm">{t("areaReports.empty")}</p>
            )}
            {KINDS.filter((kind) => reports.some((report) => report.kind === kind)).map(
              (kind) => (
                <div key={kind} className="space-y-2">
                  <h3 className="font-display text-lg">{t(`wineType.${kind}`)}</h3>
                  <ul className="border-line bg-surface divide-line divide-y rounded-sm border">
                    {groupsOf(reports, kind).map((group) => (
                      <GroupRow
                        key={`${group.countryCode}|${group.area}`}
                        group={group}
                        canRun={canRun}
                        isRunning={
                          running !== null &&
                          running.kind === group.kind &&
                          running.countryCode === group.countryCode &&
                          running.area === group.area
                        }
                        onRun={() =>
                          run({
                            kind: group.kind,
                            countryCode: group.countryCode,
                            area: group.area,
                          })
                        }
                        onPublish={publish}
                        onUnpublish={unpublish}
                        onDelete={remove}
                        onReviewed={load}
                      />
                    ))}
                  </ul>
                </div>
              ),
            )}
          </section>
        </>
      )}
    </section>
  );
}

/**
 * The open wishes, gathered by area, the most wished for first. Task 10.33.
 *
 * *Kör* starts a run with the three filled in, and the wish is met when that
 * run — or any other for the same area — is published. *Avböj* declines every
 * open wish for the area at once, with one optional line the wishers see.
 * A group that already has a draft says so, since publishing it is then the
 * cheaper answer.
 */
function OpenWishes({
  reports,
  canRun,
  onRun,
}: {
  reports: readonly AreaReportSummary[];
  canRun: boolean;
  onRun: (request: RunAreaReportRequest) => void;
}) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<OpenAreaWishGroup[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [declining, setDeclining] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    void fetchOpenAreaWishes()
      .then((body) => setGroups(body.groups))
      .catch(() => setFailed(true));
  }, []);

  useReload(load);

  const keyOf = (group: OpenAreaWishGroup) =>
    `${group.kind}|${group.countryCode}|${group.area}`;

  const decline = (group: OpenAreaWishGroup) => {
    void declineAreaWishes({
      kind: group.kind,
      countryCode: group.countryCode,
      area: group.area,
      reason,
    })
      .then(() => {
        setDeclining(null);
        setReason("");
        load();
      })
      .catch(() => setFailed(true));
  };

  const open = groups ?? [];
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl">
        {open.length > 0
          ? t("areaReports.wishes.titleCount", { count: open.length })
          : t("areaReports.wishes.title")}
      </h2>
      {failed && (
        <p role="alert" className="text-past text-sm">
          {t("areaReports.wishes.failed")}
        </p>
      )}
      {groups !== null && open.length === 0 && (
        <p className="text-muted text-sm">{t("areaReports.wishes.none")}</p>
      )}
      {open.length > 0 && (
        <ul className="border-line bg-surface divide-line max-w-3xl divide-y rounded-sm border">
          {open.map((group) => {
            const key = keyOf(group);
            const hasDraft = reports.some(
              (report) =>
                report.kind === group.kind &&
                report.countryCode === group.countryCode &&
                report.area === group.area &&
                report.status === "draft",
            );
            return (
              <li key={key} className="space-y-2 px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
                  <span className="font-display font-semibold">
                    {t(`wineType.${group.kind}`)} · {countryName(group.countryCode)} ·{" "}
                    {group.area}
                  </span>
                  <span
                    className="bg-raised rounded-full px-2 text-sm tabular-nums"
                    title={t("areaReports.wishes.countTitle")}
                  >
                    {group.wishes.length}
                  </span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    disabled={!canRun}
                    onClick={() =>
                      onRun({
                        kind: group.kind,
                        countryCode: group.countryCode,
                        area: group.area,
                      })
                    }
                    className="text-accent text-sm font-semibold underline-offset-4 hover:underline disabled:opacity-40"
                  >
                    {t("areaReports.run")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeclining(key);
                      setReason("");
                    }}
                    className="text-muted hover:text-past text-sm underline-offset-4 hover:underline"
                  >
                    {t("areaReports.wishes.decline")}
                  </button>
                </div>
                <p className="text-muted text-sm">
                  {group.wishes
                    .map((wish) => `${wish.who}, ${formatDate(wish.wishedAt)}`)
                    .join(" · ")}
                </p>
                {group.wishes
                  .filter((wish) => wish.why !== null)
                  .map((wish) => (
                    <p key={wish.id} className="text-muted text-sm italic">
                      ”{wish.why}” — {wish.who}
                    </p>
                  ))}
                {hasDraft && (
                  <p className="text-accent text-sm">
                    {t("areaReports.wishes.hasDraft")}
                  </p>
                )}
                {declining === key && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <label className="sr-only" htmlFor={`decline-${key}`}>
                      {t("areaReports.wishes.reason")}
                    </label>
                    <input
                      id={`decline-${key}`}
                      // Opened by pressing Avböj, so the line is what comes
                      // next; without focus the first keystrokes go nowhere.
                      autoFocus
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      maxLength={200}
                      placeholder={t("areaReports.wishes.reason")}
                      className={`${control} ${resting} min-w-0 flex-1 basis-60`}
                    />
                    <button
                      type="button"
                      onClick={() => decline(group)}
                      className={actionButton}
                    >
                      {t("areaReports.wishes.confirmDecline")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeclining(null)}
                      className="text-muted px-2 text-sm underline-offset-4 hover:underline"
                    >
                      {t("areaReports.wishes.cancel")}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * A new run: kind, country and area.
 *
 * The countries the administrator's cellars hold come first, then the rest,
 * each in alphabetical order (`orderCountries`); the areas already run in the chosen country
 * are offered as the area is typed, so a second run lands on the first
 * spelling — which the server does anyway, by the same key.
 */
function RunForm({
  reports,
  canRun,
  available,
  running,
  onRun,
}: {
  reports: readonly AreaReportSummary[];
  canRun: boolean;
  available: boolean;
  running: RunAreaReportRequest | null;
  onRun: (request: RunAreaReportRequest) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<WineType>("Red");
  const [countryCode, setCountryCode] = useState("FR");
  const [area, setArea] = useState("");
  const [missing, setMissing] = useState(false);

  const countryChoices = orderCountries(useCellarCountries());
  const areas = [
    ...new Set(
      reports
        .filter((report) => report.countryCode === countryCode)
        .map((report) => report.area),
    ),
  ].sort((a, b) => a.localeCompare(b, "sv"));

  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl">{t("areaReports.newRun")}</h2>
      {!available && (
        <p className="text-muted max-w-prose text-sm">{t("areaReports.cannotRun")}</p>
      )}
      <form
        className="grid max-w-3xl gap-3 sm:grid-cols-[1fr_1fr_1.4fr_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          if (area.trim() === "") {
            setMissing(true);
            return;
          }
          setMissing(false);
          onRun({ kind, countryCode, area: area.trim() });
          setArea("");
        }}
      >
        <div>
          <label className={fieldLabel} htmlFor="run-kind">
            {t("areaReports.kind")}
          </label>
          <div className="mt-1">
            <Select
              id="run-kind"
              value={kind}
              onChange={(next) => setKind(next as WineType)}
            >
              {KINDS.map((option) => (
                <option key={option} value={option}>
                  {t(`wineType.${option}`)}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <label className={fieldLabel} htmlFor="run-country">
            {t("areaReports.country")}
          </label>
          <div className="mt-1">
            <CountrySelect
              id="run-country"
              value={countryCode}
              onChange={(next) => setCountryCode(next ?? countryCode)}
              {...countryChoices}
            />
          </div>
        </div>
        <div>
          <label className={fieldLabel} htmlFor="run-area">
            {t("areaReports.area")}
          </label>
          <input
            id="run-area"
            value={area}
            onChange={(event) => setArea(event.target.value)}
            list="run-areas"
            autoComplete="off"
            maxLength={80}
            placeholder={t("areaReports.areaPlaceholder")}
            className={`${control} ${resting} mt-1`}
          />
          <datalist id="run-areas">
            {areas.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </div>
        <button type="submit" className={actionButton} disabled={!canRun}>
          {running !== null ? t("areaReports.running") : t("areaReports.run")}
        </button>
      </form>
      {missing && (
        <p role="alert" className="text-past text-sm">
          {t("areaReports.areaMissing")}
        </p>
      )}
      {running !== null && (
        <p role="status" className="text-muted text-sm">
          {t("areaReports.runningLong", { area: running.area })}
        </p>
      )}
    </section>
  );
}

function GroupRow({
  group,
  canRun,
  isRunning,
  onRun,
  onPublish,
  onUnpublish,
  onDelete,
  onReviewed,
}: {
  group: Group;
  canRun: boolean;
  isRunning: boolean;
  onRun: () => void;
  onPublish: (report: AreaReportSummary) => void;
  onUnpublish: (report: AreaReportSummary) => void;
  onDelete: (report: AreaReportSummary) => void;
  onReviewed: () => void;
}) {
  const { t } = useTranslation();
  const [reviewing, setReviewing] = useState<string | null>(null);
  const published = group.runs.find((run) => run.status === "published");
  // The runs come newest first. *Granskad* on the group means the latest
  // version has been checked — task 10.48 — not that some run once was: a
  // rerun after a check is a new text nobody has checked.
  const latest = group.runs.find((run) => run.status !== "running");
  const outNow = isRunning || group.runs.some((run) => run.status === "running");

  return (
    <li className="space-y-3 px-4 py-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-display font-semibold">{group.area}</span>
        <span className="text-muted inline-flex items-center gap-1.5 text-sm">
          <Flag countryCode={group.countryCode} />
          {countryName(group.countryCode)}
        </span>
        {published !== undefined ? (
          <span className="border-ready text-ready rounded-full border px-2 py-px text-xs">
            {t("areaReports.publishedOn", { date: formatDate(published.publishedAt) })}
          </span>
        ) : (
          <span className="border-line text-muted rounded-full border px-2 py-px text-xs">
            {t("areaReports.notPublished")}
          </span>
        )}
        {latest !== undefined && latest.reviewedAt !== null && (
          <span
            className="border-ready text-ready rounded-full border px-2 py-px text-xs"
            title={t("areaReports.reviewedTitle")}
          >
            {t("areaReports.reviewedOn", { date: formatDate(latest.reviewedAt) })}
          </span>
        )}
        {outNow && (
          <span className="border-accent text-accent rounded-full border px-2 py-px text-xs">
            {t("areaReports.running")}
          </span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onRun}
          disabled={!canRun || outNow}
          className="text-accent text-sm underline-offset-4 hover:underline disabled:opacity-40 disabled:hover:no-underline"
        >
          {t("areaReports.runAgain")}
        </button>
      </div>
      <ul className="border-line ml-1 space-y-1 border-l-2 pl-4">
        {group.runs.map((run) => (
          <li
            key={run.id}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
          >
            <span>
              <span className="tabular-nums">{formatTime(run.runAt)}</span>{" "}
              <span
                className={
                  run.status === "published" ? "text-ready font-semibold" : "text-muted"
                }
              >
                ·{" "}
                {run.status === "published"
                  ? t("areaReports.status.published")
                  : run.status === "running"
                    ? t("areaReports.running")
                    : run.wasPublished
                      ? t("areaReports.status.wasPublished")
                      : t("areaReports.status.draft")}
              </span>
              {/* What earlier fact checks told this run, so a rerun that
                  repeats a corrected mistake is read knowing it was told. */}
              {run.reviewedAt !== null && (
                <span className="text-muted"> · {t("areaReports.status.reviewed")}</span>
              )}
              {run.correctionCount > 0 && (
                <span className="text-muted" title={t("areaReports.correctionsTitle")}>
                  {" "}
                  · {t("areaReports.corrections", { count: run.correctionCount })}
                </span>
              )}
            </span>
            {run.status !== "running" && (
              <span className="flex flex-wrap items-baseline gap-x-4">
                <Link
                  to={`/upptack/${encodeURIComponent(run.id)}`}
                  className="text-accent underline-offset-4 hover:underline"
                >
                  {t("areaReports.read")}
                </Link>
                <button
                  type="button"
                  onClick={() => setReviewing(reviewing === run.id ? null : run.id)}
                  aria-expanded={reviewing === run.id}
                  className="text-accent underline-offset-4 hover:underline"
                >
                  {reviewing === run.id
                    ? t("areaReports.closeReview")
                    : t("areaReports.review")}
                </button>
                {run.status === "draft" && (
                  <button
                    type="button"
                    onClick={() => onPublish(run)}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("areaReports.publish")}
                  </button>
                )}
                {/* The area leaves the readers' tree; the report stays, a
                    draft, until it is published again or deleted. */}
                {run.status === "published" && (
                  <button
                    type="button"
                    onClick={() => onUnpublish(run)}
                    className="text-muted hover:text-past underline-offset-4 hover:underline"
                  >
                    {t("areaReports.unpublish")}
                  </button>
                )}
                {run.status === "draft" && (
                  <button
                    type="button"
                    onClick={() => onDelete(run)}
                    className="text-muted hover:text-past underline-offset-4 hover:underline"
                  >
                    {t("areaReports.delete")}
                  </button>
                )}
              </span>
            )}
            {reviewing === run.id && (
              <div className="basis-full pt-2">
                <RunReview reportId={run.id} onReviewed={onReviewed} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

/**
 * The fact check of one run, opened under it — moved here from the report's
 * own page on 2026-09-29, asked for by the owner: checking is an
 * administrator's act beside publishing and unpublishing, and the report's
 * page then reads exactly as the readers will read it.
 *
 * The list holds summaries, so the run is fetched whole when the panel opens:
 * the check needs the content, and the review it last had.
 */
function RunReview({
  reportId,
  onReviewed,
}: {
  reportId: string;
  onReviewed: () => void;
}) {
  const { t } = useTranslation();
  const [report, setReport] = useState<AreaReport | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by *Försök igen*: the fetch runs again without closing the panel.
  // A failure here has so far been passing — a deploy switching revisions, a
  // cold start — and the second try is what the administrator would do anyway.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setFailed(false);
    void fetchAreaReport(reportId)
      .then((body) => {
        if (current) setReport(body);
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [reportId, attempt]);

  if (failed)
    return (
      <p role="alert" className="text-past text-sm">
        {t("areaReview.loadFailed")}{" "}
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="text-accent font-semibold underline-offset-4 hover:underline"
        >
          {t("areaReview.retry")}
        </button>
      </p>
    );
  if (report === null) return <p className="text-muted text-sm">{t("loading")}</p>;
  return (
    <div className="space-y-3">
      {report.corrections.length > 0 && (
        // What this run was told by earlier checks — read beside a new
        // check, it shows whether a corrected mistake came back anyway.
        <details className="border-line rounded-sm border px-4 py-3 text-sm">
          <summary className="text-accent cursor-pointer">
            {t("areaReports.correctionsCarried", { count: report.corrections.length })}
          </summary>
          <ul className="mt-2 space-y-2">
            {report.corrections.map((correction) => (
              <li key={`${correction.part}|${correction.problem}`}>
                <span className="text-muted">{correction.part}: </span>
                {correction.problem} {correction.correction}
              </li>
            ))}
          </ul>
        </details>
      )}
      <AreaReview
        report={report}
        onChange={(next) => {
          setReport(next);
          // The list's *Granskad* follows the check without a reload.
          onReviewed();
        }}
      />
    </div>
  );
}

/** The groups of one kind, by country name and then area. */
function groupsOf(reports: readonly AreaReportSummary[], kind: WineType): Group[] {
  const groups = new Map<string, Group>();
  for (const report of reports) {
    if (report.kind !== kind) continue;
    const key = `${report.countryCode}|${report.area}`;
    const group = groups.get(key);
    if (group === undefined) {
      groups.set(key, {
        kind,
        countryCode: report.countryCode,
        area: report.area,
        runs: [report],
      });
    } else {
      groups.set(key, { ...group, runs: [...group.runs, report] });
    }
  }
  return [...groups.values()].sort(
    (a, b) =>
      countryName(a.countryCode).localeCompare(countryName(b.countryCode), locale()) ||
      a.area.localeCompare(b.area, "sv"),
  );
}

function formatDate(iso: string | null): string {
  if (iso === null) return "";
  return new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(locale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
